import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../../shared/ui/Seo";
import { getCustomer } from "../api";
import AccountSidebar from "./AccountSidebar";
import EditProfileModal from "./EditProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import "../styles/my-account.css";

const FIELDS = [
  ["firstName", "First name"],
  ["lastName", "Last name"],
  ["userName", "Username"],
  ["displayName", "Display name"],
  ["email", "Email"],
  ["phoneNumber", "Phone number"],
];

const SHORTCUTS = [
  { to: "/my-orders", icon: "ph-package", title: "My Orders", text: "Track orders and download invoices" },
  { to: "/address-book", icon: "ph-map-pin", title: "Address Book", text: "Manage delivery and billing addresses" },
  { to: "/wishlist", icon: "ph-heart", title: "Wishlist", text: "Products you saved for later" },
];

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const MyAccount = () => {
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setIsError(false);
    getCustomer()
      .then((response) => {
        if (!ignore) setCustomer(response);
      })
      .catch((error) => {
        console.error("Error fetching customer:", error.message);
        if (!ignore) setIsError(true);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  // Receives the updated customer from the edit modal.
  const handleEditProfileSave = (updatedCustomer) => {
    setCustomer(updatedCustomer);
    toast.success("Profile updated successfully.");
  };

  const fullName = customer
    ? [customer.firstName, customer.lastName].filter(hasValue).join(" ") || customer.displayName
    : "";
  const initials = (fullName || customer?.email || "?")
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  let content;
  if (isLoading) {
    content = (
      <div className="ma-card ma-profile" aria-hidden="true">
        <span className="ma-avatar skeleton-block" />
        <div className="ma-profile__info">
          <span className="skeleton-line w-120" />
          <span className="skeleton-line w-200" />
        </div>
      </div>
    );
  } else if (isError || !customer) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-user-circle-dashed ui-empty__icon" />
        <h2 className="ui-empty__title">We couldn't load your account</h2>
        <p className="ui-empty__text">Please check your connection and try again.</p>
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </div>
    );
  } else {
    content = (
      <>
        <section className="ma-card ma-profile">
          <span className="ma-avatar" aria-hidden="true">{initials}</span>
          <div className="ma-profile__info">
            {fullName && <h2 className="ma-profile__name">{fullName}</h2>}
            {hasValue(customer.email) && <p className="ma-profile__email">{customer.email}</p>}
          </div>
          <div className="ma-profile__actions">
            <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setEditModalOpen(true)}>
              <i className="ph ph-pencil-simple-line" />
              Edit profile
            </button>
            <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setChangePasswordModalOpen(true)}>
              <i className="ph ph-lock-key" />
              Change password
            </button>
          </div>
        </section>

        <section className="ma-card">
          <h2 className="ma-card__title">Personal information</h2>
          <dl className="ma-fields">
            {FIELDS.map(([key, label]) => (
              <div key={key} className="ma-field">
                <dt>{label}</dt>
                <dd>{hasValue(customer[key]) ? customer[key] : "—"}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="ma-shortcuts">
          {SHORTCUTS.map(({ to, icon, title, text }) => (
            <Link key={to} to={to} className="ma-shortcut">
              <span className="ma-shortcut__icon">
                <i className={`ph ${icon}`} aria-hidden="true" />
              </span>
              <span className="ma-shortcut__body">
                <span className="ma-shortcut__title">{title}</span>
                <span className="ma-shortcut__text">{text}</span>
              </span>
              <i className="ph ph-caret-right ma-shortcut__arrow" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </>
    );
  }

  return (
    <section className="account-page py-20">
      <Seo title="My Account" noindex />
      <div className="account-layout">
        <AccountSidebar />
        <div className="account-main ma-main">
          <div className="ui-head">
            <h1 className="ui-head__title">My Account</h1>
            <p className="ui-head__text">Manage your profile, password and saved details.</p>
          </div>
          {content}
          <EditProfileModal
            isOpen={isEditModalOpen}
            onClose={() => setEditModalOpen(false)}
            customer={customer}
            onSave={handleEditProfileSave}
          />
          <ChangePasswordModal
            isOpen={isChangePasswordModalOpen}
            onClose={() => setChangePasswordModalOpen(false)}
            email={customer?.email}
          />
        </div>
      </div>
    </section>
  );
};

export default MyAccount;
